import { MessageAudience, OccurrenceMessage } from './occurrence.models';

/**
 * Uma conversa das Comunicações: a mensagem que a abriu e as que lhe responderam.
 *
 * <p>"Envio o email com subject A, quando me respondem a este deveria ficar dentro deste e com o
 * mesmo subject, depois ter à frente um botão responder" (revisão de 2026-10-19). Antes a lista
 * era plana, e um pedido de elementos e o seguimento da ação 2 misturavam-se por ordem de data.
 */
export interface MessageThread {
  readonly key: string;
  /** O assunto da mensagem que abriu a conversa — é com ele que se responde. */
  readonly subject: string;
  readonly audience: MessageAudience;
  /** Pela ordem em que aconteceram. */
  readonly messages: readonly OccurrenceMessage[];
  readonly lastAt: string;
  /** A outra parte, a quem vai a resposta. Null no acompanhamento, que não tem endereço. */
  readonly replyTo: string | null;
}

/** "RE:", "Res:", "FW:", "Enc:"… — o que os clientes de e-mail põem à frente ao responder. */
const REPLY_PREFIX = /^\s*(re|res|fw|fwd|enc|tr|rv|aw|wg)\s*:\s*/i;

/**
 * O assunto que identifica a conversa: sem os prefixos de resposta, sem o código entre
 * parênteses retos (a resposta pode trazê-lo ou não) e sem diferenças de espaços e maiúsculas.
 */
export function threadSubject(subject: string): string {
  let rest = subject ?? '';
  while (REPLY_PREFIX.test(rest)) rest = rest.replace(REPLY_PREFIX, '');
  return rest.replace(/\[[^\]]*\]/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase();
}

/** As mensagens em conversas: as de cada conversa por ordem, as conversas da mais recente. */
export function groupThreads(messages: readonly OccurrenceMessage[]): MessageThread[] {
  const ordered = [...messages].sort((a, b) => a.sentAt.localeCompare(b.sentAt) || a.id - b.id);
  const byKey = new Map<string, OccurrenceMessage[]>();
  for (const message of ordered) {
    // A interna e a do acompanhamento não se juntam a uma externa com o mesmo assunto: são
    // conversas com outras pessoas.
    const key = `${message.audience}|${threadSubject(message.subject)}`;
    const thread = byKey.get(key);
    if (thread) thread.push(message);
    else byKey.set(key, [message]);
  }
  return [...byKey.entries()]
    .map(([key, list]) => {
      const first = list[0];
      const last = list[list.length - 1];
      const replyTo = first.audience === 'PORTAL' ? null
        : last.direction === 'RECEBIDA' ? last.fromAddress : last.toAddress;
      return {
        key, subject: first.subject, audience: first.audience, messages: list,
        lastAt: last.sentAt, replyTo,
      };
    })
    .sort((a, b) => b.lastAt.localeCompare(a.lastAt));
}
